import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";
import { markEventDeleted, unmarkEventDeleted, withoutDeletedEvents } from "../../components/content/Events/lib/deletedEvents";

export const eventsApi = createApi({
  reducerPath: "eventsApi",
  // keepUnusedDataFor: 3600,
  tagTypes: ["Events", "EventMessages", "EventAttendees", "EventFriendGuests", "EventTournaments", "EventMeetBanner", "EventWhenInvited", "EventInterestSelection", "EventCalendarSetting"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({

    // Optional scope ("active" | "past") trims the list server-side so the
    // page no longer loads a user's entire event history on every open. Called
    // with no arg it returns all events (unchanged), keeping existing callers
    // (CreatedEvents, EventGeofencePrompt) working. RTK Query caches each scope
    // separately, so the Past tab fetches lazily once and is then cached.
    getEvents: builder.query({
      query: (scope) =>
        "/events/portal/all-events" + (scope ? `?scope=${scope}` : ""),
      // Glitch #645: drop rows for events this browser just deleted, so a
      // refetch that still reads the pre-sync mirror cannot bring them back.
      transformResponse: (response) =>
        Array.isArray(response?.data) ? { ...response, data: withoutDeletedEvents(response.data) } : response,
      providesTags: ["Events"],
    }),

    createNewEvent: builder.mutation({
      query: (payload) => ({
        url: "/events/portal/create-new-event",
        method: "POST",
        body: payload,
      }),
      // NOTE: intentionally NOT invalidatesTags(["Events"]) here. A single
      // post-create refetch raced ahead of Airtable's read-after-write /
      // view-membership lag, so the brand-new event was missing from the list
      // and only appeared on a later manual reload. CreateEvent now does an
      // optimistic insert into the getEvents cache and reconciles with a
      // bounded background re-pull (see CreateEvent.jsx handleSubmit), which
      // both shows the event instantly AND guarantees it lands.
    }),

    updateEvent: builder.mutation({
      query: (payload) => ({
        url: "/events/portal/update-event",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        "Events",
        { type: "EventAttendees", id: payload?.id },
        // The save may have attached / changed / removed tournaments
        // (attached_tournament_ids, thread #1703 r2), so the Edit screen's
        // seed must not be served from a pre-save cache.
        { type: "EventTournaments", id: payload?.id },
        // Same reason for the Invite Link banner (thread #1734): a save that
        // sends `hide_meet_banner` must not leave the pre-save value in cache,
        // or re-opening the screen inside the cache window seeds the toggle to
        // the OLD state and the host's next save flips it back.
        { type: "EventMeetBanner", id: payload?.id },
        // Same reason for "When Invited" (Levi 2026-10-31): a save that sends
        // `when_invited` must not re-seed the Edit chip from a pre-save cache.
        { type: "EventWhenInvited", id: payload?.id },
        // And for Add Interest Selection (thread 1a19d2aae8003ea5).
        { type: "EventInterestSelection", id: payload?.id },
        // And for the Google Calendar on/off switch (Levi 2026-11-05).
        { type: "EventCalendarSetting", id: payload?.id },
      ],
    }),

    // Permanently delete a host event. Server cascades to invitee
    // sub-tasks, linked Event Reminders, and the GCal event. Host-only
    // (granted editors get 403 server-side). Wired into EventDetails's
    // Update-mode footer next to "Update Event".
    deleteEvent: builder.mutation({
      query: (hostRecordId) => ({
        url: `/events/portal/${hostRecordId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Events"],
      // Glitch #645: tombstone BEFORE the request, so the refetch this mutation
      // triggers on success is already filtered. On failure, lift the tombstone
      // and re-read the list, since the event was not deleted.
      async onQueryStarted(hostRecordId, { dispatch, queryFulfilled }) {
        markEventDeleted(hostRecordId);
        try {
          await queryFulfilled;
        } catch {
          unmarkEventDeleted(hostRecordId);
          dispatch(eventsApi.util.invalidateTags(["Events"]));
        }
      },
    }),

    getEventAttendees: builder.query({
      query: (hostRecordId) => `/events/portal/event-attendees/${hostRecordId}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventAttendees", id: hostRecordId }],
    }),

    // Friends on an event's guest list (the Event Attendance rows the scan
    // code, "Invite to Event" and My Friends' Event button write) — a
    // separate list from the invitee sub-tasks getEventAttendees returns.
    // Host or President only (BFF 403s anyone else). Thread #1596.
    getEventFriendGuests: builder.query({
      query: (hostRecordId) => `/user/portal/my-friends/event/${encodeURIComponent(hostRecordId)}/guests`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventFriendGuests", id: hostRecordId }],
    }),

    // The host's attendance scan code (thread 1a18333c798c828b): a signed link
    // a guest's phone opens to check in. Signed in; the BFF refuses anyone who
    // may not add guests, and refuses outside the window (1h before start to
    // 4h after end). Not cached: each fetch mints a fresh signed link.
    getAttendanceScanLink: builder.query({
      query: (hostRecordId) => `/events/portal/${encodeURIComponent(hostRecordId)}/attendance-scan-link`,
      keepUnusedDataFor: 0,
    }),

    // Take one Friend off the event (database first, backup copy after).
    removeEventFriendGuest: builder.mutation({
      query: ({ hostRecordId, attendanceRecId }) => ({
        url: `/user/portal/my-friends/event/${encodeURIComponent(hostRecordId)}/guest/${encodeURIComponent(attendanceRecId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { hostRecordId }) => [{ type: "EventFriendGuests", id: hostRecordId }],
    }),

    saveEventAttendance: builder.mutation({
      query: (payload) => ({
        url: "/events/portal/save-attendance",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Events"],
    }),

    grantEditAccess: builder.mutation({
      query: (payload) => ({
        url: "/events/portal/grant-edit-access",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Events"],
    }),

    getGrantedEditors: builder.query({
      query: (hostRecordId) => `/events/portal/granted-editors/${hostRecordId}`,
    }),

    // Tournaments attached to this event — the Edit screen's picker seed
    // (thread #1703 r2). Returns the host row's Event Master pointer, whether
    // this record is even an event (a sub-task cannot own tournaments), and
    // one row per attached tournament already resolved to the STANDALONE
    // (source) recId, which is what the picker selects and what the admin
    // screen is keyed on. The synced-recId translation stays server-side.
    getEventTournaments: builder.query({
      query: (hostRecordId) => `/events/portal/event-tournaments/${encodeURIComponent(hostRecordId)}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventTournaments", id: hostRecordId }],
    }),

    // Whether this event's Invite Link shows the JOIN - VIDEO MEET banner
    // (thread #1734). Seeds the Edit screen's toggle. MySQL-only server-side
    // (portal_app.event_meet_banner) — it cannot ride the generic
    // /task/portal/get-task read, which serves every task type, and it has no
    // Airtable or mirror column behind it by design. Absent row = `hidden:
    // false` = banner shown, which is also the pre-change behaviour for every
    // event created before the setting existed.
    getEventMeetBanner: builder.query({
      query: (hostRecordId) => `/events/portal/event-meet-banner/${encodeURIComponent(hostRecordId)}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventMeetBanner", id: hostRecordId }],
    }),

    // Google Calendar on/off for an existing event (Levi 2026-11-05). Seeds the
    // event form's switch: on = the event has a calendar event whose updates are
    // not switched off. Absent setting = updates on (every older event).
    getEventCalendarSetting: builder.query({
      query: (hostRecordId) => `/events/portal/event-calendar-setting/${encodeURIComponent(hostRecordId)}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventCalendarSetting", id: hostRecordId }],
    }),

    // "When Invited" saved on the event (Levi 2026-10-31). Seeds the Edit Event chip
    // with the choice Create made, so Edit shows and changes the same control.
    getEventWhenInvited: builder.query({
      query: (hostRecordId) => `/events/portal/event-when-invited/${encodeURIComponent(hostRecordId)}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventWhenInvited", id: hostRecordId }],
    }),

    // Add Interest Selection (thread 1a19d2aae8003ea5). Seeds the Edit Event card.
    getEventInterestSelection: builder.query({
      query: (hostRecordId) => `/events/portal/event-interest/${encodeURIComponent(hostRecordId)}`,
      providesTags: (result, error, hostRecordId) => [{ type: "EventInterestSelection", id: hostRecordId }],
    }),

    // E-Stats activity log (Levi 2026-11-05): invite texts, form fills, scans, check-ins
    // and tournament sign-ups for this event. Host or granted editor only.
    getEventActivityLog: builder.query({
      query: (hostRecordId) => `/events/portal/event-activity/${encodeURIComponent(hostRecordId)}`,
    }),

    // Phase A: read-only event chat. EventChatPanel re-reads this when the
    // live push (GET /:eventId/messages/stream) says the chat moved; it sets
    // a timed `pollingInterval` only while that push is not connected, and
    // never while the drawer is closed.
    getEventMessages: builder.query({
      query: (eventId) => `/events/portal/${eventId}/messages`,
      providesTags: (result, error, eventId) => [{ type: "EventMessages", id: eventId }],
    }),

    // Phase B: chat writes. Each invalidates the per-event message list so
    // the open drawer refetches immediately rather than waiting for the
    // 12s poll. Edit/delete pass `eventId` only for the invalidation tag.
    //
    // Phase H: payloads are now JSON-only. Attachments are referenced by
    // `attachmentKeys` — the GCS object keys returned by the /presign
    // endpoint after the browser has already uploaded each blob directly to
    // GCS. The BFF re-signs each key for read just before passing to
    // Airtable, so blobs stay private (no makePublic). Cloud Run never
    // touches the file bytes, so the 32 MiB HTTP/1 ingress ceiling no
    // longer caps attachment size.
    postEventMessage: builder.mutation({
      query: ({ eventId, body, attachmentKeys }) => ({
        url: `/events/portal/${eventId}/messages`,
        method: "POST",
        body: {
          body: typeof body === "string" ? body : "",
          attachmentKeys: Array.isArray(attachmentKeys) ? attachmentKeys : [],
        },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: "EventMessages", id: eventId }],
    }),

    // Phase B: body-only edit (JSON path).
    // Phase D: same endpoint, additionally accepts an array of attachment
    // ids to remove (`removeAttachmentIds`) and/or new attachments to add.
    // Phase H: new attachments are referenced by `addAttachmentKeys`
    // (object keys from the /presign endpoint) instead of raw File buffers.
    editEventMessage: builder.mutation({
      query: ({ eventId, messageId, body, addAttachmentKeys, removeAttachmentIds }) => {
        const payload = {};
        if (typeof body === "string") payload.body = body;
        if (Array.isArray(addAttachmentKeys) && addAttachmentKeys.length > 0) {
          payload.addAttachmentKeys = addAttachmentKeys;
        }
        if (Array.isArray(removeAttachmentIds) && removeAttachmentIds.length > 0) {
          payload.removeAttachmentIds = removeAttachmentIds;
        }
        return {
          url: `/events/portal/${eventId}/messages/${messageId}`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: "EventMessages", id: eventId }],
    }),

    // Phase H: mint a presigned POST URL for ONE chat attachment. Returns
    // {url, fields, objectKey, maxBytes}. The browser uploads the file
    // directly to that URL via multipart/form-data; on success the caller
    // submits the message with `attachmentKeys: [objectKey, ...]`.
    generateChatAttachmentPresign: builder.mutation({
      query: ({ eventId, filename, contentType, size }) => ({
        url: `/events/portal/${eventId}/messages/presign`,
        method: "POST",
        body: { filename, contentType, size },
      }),
    }),

    deleteEventMessage: builder.mutation({
      query: ({ eventId, messageId }) => ({
        url: `/events/portal/${eventId}/messages/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: "EventMessages", id: eventId }],
    }),

    // Phase E: persist a per-(employee, event) read receipt so the unread
    // badge on EventListView and the dot on the chat pull-tab survive
    // reloads/devices. Empty body — server timestamps `now`. Invalidates
    // both the per-event message tag (so unreadCount in the GET response
    // refreshes) and the events list (so the badge clears in the list view).
    markEventChatSeen: builder.mutation({
      query: ({ eventId }) => ({
        url: `/events/portal/${eventId}/seen`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (result, error, { eventId }) => [
        { type: "EventMessages", id: eventId },
        "Events",
      ],
    }),

    // Phase 12.4c — autocomplete source for the "Pick existing Lead" picker
    // in CreateEvent's ExternalInviteesSection. Caller-owned leads (Lead
    // Owner = caller) surface first; results capped at 25 server-side.
    searchExistingLeads: builder.query({
      query: ({ q, limit }) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (limit) params.set("limit", String(limit));
        return `/user/portal/leads/${params.toString() ? `search?${params.toString()}` : "search"}`;
      },
    }),

    // Phase 12.7 — Lead Source dropdown options for the "Add new Lead" form
    // in CreateEvent's ExternalInviteesSection. Served from the fast MySQL
    // mirror; cached for the session since the option set rarely changes.
    getLeadSourceOptions: builder.query({
      query: () => "/user/portal/leads/lead-source-options",
      keepUnusedDataFor: 60 * 30,
    }),

    // Thread #1640 — the Add Friend form's own scan code: the signed-in rep's
    // standing, rep-unique code. The URL is derived from a fixed annual expiry,
    // so it is the same URL every time until it rotates; cached like the tag
    // options. Never invalidated — nothing the rep does changes it.
    getFriendScanLink: builder.query({
      query: () => "/user/portal/my-friends/scan-link",
      keepUnusedDataFor: 60 * 30,
    }),

    // Phase 12.4c — invite an existing Sales Lead by recId to an Event.
    // Idempotent: re-inviting the same Lead returns the existing attendance.
    // No tag invalidation here — CreateEvent does manual MyLeads invalidation
    // via userApi after the create+invite sequence resolves (the lead already
    // exists in My Leads; the attendance write doesn't change ownership).
    inviteExistingLead: builder.mutation({
      query: ({ eventRecId, leadRecId }) => ({
        url: `/events/portal/invite-existing-lead/${eventRecId}`,
        method: "POST",
        body: { leadRecId },
      }),
    }),

    // Lead self-booking — PR-5b. Leads-only autocomplete for the Event List
    // "referred-by" picker. Caller-owned leads surface first; served from the
    // MySQL mirror, capped server-side. Lazy: fired on debounced typing.
    getReferredByOptions: builder.query({
      query: ({ q, limit }) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (limit) params.set("limit", String(limit));
        const qs = params.toString();
        return `/events/portal/referred-by-options${qs ? `?${qs}` : ""}`;
      },
    }),

    // Lead self-booking — PR-5b. Assign the chosen referrer (an existing Lead)
    // onto the subject lead's existing "Referred By (Lead)" field — a live
    // Airtable write. Both ids are Sales Lead recIds.
    assignReferredBy: builder.mutation({
      query: ({ leadRecId, referredByLeadRecId }) => ({
        url: "/events/portal/referred-by",
        method: "POST",
        body: { leadRecId, referredByLeadRecId },
      }),
    }),

    // Phase 3 — geofenced self-check-in. Fired by EventGeofencePrompt when
    // the rep is physically inside the host event's Venue Geo. The BFF
    // re-validates the fence and flips ONLY the caller to Attended; the
    // tap is idempotent on a second press. Invalidates Events so the
    // EventListView attendance badge refreshes immediately.
    selfCheckInEvent: builder.mutation({
      query: ({ hostRecordId, geo }) => ({
        url: "/events/portal/self-check-in",
        method: "POST",
        body: { hostRecordId, geo },
      }),
      invalidatesTags: ["Events"],
    }),

    // Phase 1B — browser-restricted Google Places API key for the Create-
    // Event Location autocomplete. Cached forever via keepUnusedDataFor
    // (key value doesn't rotate per-session); the key itself is referrer-
    // restricted to portal.kindredpest.com/* so a JS-bundle leak is inert.
    getGooglePlacesKey: builder.query({
      query: () => "/events/portal/google-places-key",
      keepUnusedDataFor: 60 * 60,
    }),

    // Phase 3 of Events-Recurring — propagate Edit Series modal edits onto
    // every FUTURE materialized occurrence (Data Input + GCal). Past
    // occurrences are server-side rejected from the walk regardless of
    // payload. Invalidates "Events" so CreatedEvents re-pulls with the
    // new field values reflected.
    propagateSeriesEdits: builder.mutation({
      query: ({ seriesId, body }) => ({
        url: `/events/portal/series/${seriesId}/propagate`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Events"],
    }),

  }),
});

export const {
  useGetEventsQuery,
  useCreateNewEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useGetEventAttendeesQuery,
  useGetEventTournamentsQuery,
  useGetEventMeetBannerQuery,
  useGetEventCalendarSettingQuery,
  useGetEventWhenInvitedQuery,
  useGetEventInterestSelectionQuery,
  useGetEventActivityLogQuery,
  useGetEventFriendGuestsQuery,
  useGetAttendanceScanLinkQuery,
  useRemoveEventFriendGuestMutation,
  useSaveEventAttendanceMutation,
  useGrantEditAccessMutation,
  useGetGrantedEditorsQuery,
  useGetEventMessagesQuery,
  usePostEventMessageMutation,
  useEditEventMessageMutation,
  useDeleteEventMessageMutation,
  useMarkEventChatSeenMutation,
  useGenerateChatAttachmentPresignMutation,
  useSearchExistingLeadsQuery,
  useLazySearchExistingLeadsQuery,
  useGetLeadSourceOptionsQuery,
  useGetFriendScanLinkQuery,
  useSelfCheckInEventMutation,
  useInviteExistingLeadMutation,
  useGetGooglePlacesKeyQuery,
  usePropagateSeriesEditsMutation,
  useLazyGetReferredByOptionsQuery,
  useAssignReferredByMutation,
} = eventsApi;